<section class="getting-started-hero">
    <div class="container">
        <div class="row">
            <div class="col-lg-8">
                <div class="hero-content">
                    <div class="breadcrumb-nav">
                        <a routerLink="/documentation">Documentation</a>
                        <span>/</span>
                        <span>Getting Started</span>
                    </div>
                    <span class="hero-badge">Beginner Guide</span>
                    <h1 class="hero-title">Getting Started with Insightful IT Services</h1>
                    <p class="hero-description">Learn how to create your first cloud instance, configure basic settings, and deploy your applications with our comprehensive step-by-step guide.</p>
                </div>
            </div>
        </div>
    </div>
</section>

<section class="documentation-content">
    <div class="container">
        <div class="content-container">
            <div class="documentation-sidebar">
                <h3 class="sidebar-title">Documentation</h3>
                <ul class="sidebar-menu">
                    <li><a routerLink="/documentation/getting-started" class="active"><i class="bx bx-rocket"></i> Getting Started</a></li>
                    <li><a routerLink="/documentation/api"><i class="bx bx-code"></i> API Documentation</a></li>
                    <li><a routerLink="/documentation/cloud-services"><i class="bx bx-server"></i> Cloud Services</a></li>
                    <li><a routerLink="/documentation/security"><i class="bx bx-shield"></i> Security & Compliance</a></li>
                    <li><a routerLink="/documentation/management"><i class="bx bx-cog"></i> Management & Monitoring</a></li>
                    <li><a routerLink="/documentation/optimization"><i class="bx bx-trending-up"></i> Performance Optimization</a></li>
                </ul>
                
                <div class="table-of-contents">
                    <h4 class="toc-title">Table of Contents</h4>
                    <ul class="toc-list">
                        <li><a href="#create-account"><i class="bx bx-chevron-right"></i> Creating Your Account</a></li>
                        <li><a href="#first-instance"><i class="bx bx-chevron-right"></i> Deploying Your First Instance</a></li>
                        <li><a href="#configure-settings"><i class="bx bx-chevron-right"></i> Basic Configuration</a></li>
                        <li><a href="#deploy-app"><i class="bx bx-chevron-right"></i> Deploying Applications</a></li>
                        <li><a href="#monitoring"><i class="bx bx-chevron-right"></i> Monitoring & Management</a></li>
                    </ul>
                </div>
            </div>
            
            <div class="documentation-main">
                <div class="article-header">
                    <h1 class="article-title">Getting Started Guide</h1>
                    <div class="article-meta">
                        <div class="meta-item">
                            <i class="bx bx-time"></i>
                            <span>Estimated reading time: 15 minutes</span>
                        </div>
                        <div class="meta-item">
                            <i class="bx bx-calendar"></i>
                            <span>Last updated: September 12, 2025</span>
                        </div>
                        <div class="meta-item">
                            <i class="bx bx-user"></i>
                            <span>Beginner Level</span>
                        </div>
                    </div>
                    <p>This guide will walk you through the essential steps to get started with Insightful IT Services cloud infrastructure. By the end of this tutorial, you'll have deployed your first instance and configured basic settings.</p>
                </div>
                
                <div class="article-content">
                    <h2 id="create-account">Creating Your Account</h2>
                    <p>Before you can start using our cloud services, you'll need to create an account. Follow these simple steps:</p>
                    
                    <ol>
                        <li>Visit our <a routerLink="/sign-up">sign-up page</a> and fill in your basic information</li>
                        <li>Verify your email address by clicking the confirmation link sent to your inbox</li>
                        <li>Complete your profile by adding payment information</li>
                        <li>Explore the dashboard to familiarize yourself with the interface</li>
                    </ol>
                    
                    <p>Once your account is created, you'll have access to our full suite of cloud services with a 30-day free trial for most services.</p>
                    
                    <h2 id="first-instance">Deploying Your First Instance</h2>
                    <p>Now that you have an account, let's deploy your first cloud instance:</p>
                    
                    <ol>
                        <li>Log in to your dashboard</li>
                        <li>Navigate to the "Instances" section</li>
                        <li>Click the "Create Instance" button</li>
                        <li>Choose your preferred operating system (Windows or Linux)</li>
                        <li>Select the instance size based on your requirements</li>
                        <li>Configure storage options (SSD, HDD, etc.)</li>
                        <li>Review your configuration and click "Deploy"</li>
                    </ol>
                    
                    <p>Your instance will be provisioned within minutes. You'll receive an email notification when it's ready.</p>
                    
                    <h3>Instance Configuration Options</h3>
                    <p>When configuring your instance, consider the following options:</p>
                    
                    <ul>
                        <li><strong>vCPUs:</strong> Choose based on your application's processing requirements</li>
                        <li><strong>RAM:</strong> Allocate sufficient memory for your applications</li>
                        <li><strong>Storage:</strong> Select SSD for high-performance applications</li>
                        <li><strong>Network:</strong> Configure firewall rules and IP settings</li>
                        <li><strong>Security:</strong> Enable additional security features as needed</li>
                    </ul>
                    
                    <h2 id="configure-settings">Basic Configuration</h2>
                    <p>After your instance is deployed, you'll need to configure some basic settings:</p>
                    
                    <h3>Network Configuration</h3>
                    <p>Configure your network settings to ensure proper connectivity:</p>
                    
                    <pre><code># Example network configuration for Linux instances
sudo nano /etc/network/interfaces

# Add the following configuration:
auto eth0
iface eth0 inet static
    address 192.168.1.100
    netmask 255.255.255.0
    gateway 192.168.1.1
    dns-nameservers 8.8.8.8 8.8.4.4</code></pre>
                    
                    <h3>Security Settings</h3>
                    <p>Enable essential security features:</p>
                    
                    <ul>
                        <li>Configure SSH keys for secure access</li>
                        <li>Set up firewall rules to restrict access</li>
                        <li>Enable automatic security updates</li>
                        <li>Configure backup schedules</li>
                    </ul>
                    
                    <h2 id="deploy-app">Deploying Applications</h2>
                    <p>Once your instance is configured, you can deploy your applications:</p>
                    
                    <ol>
                        <li>Connect to your instance via SSH or RDP</li>
                        <li>Install necessary dependencies and software</li>
                        <li>Deploy your application files</li>
                        <li>Configure application settings</li>
                        <li>Test your deployment</li>
                    </ol>
                    
                    <h3>Example: Deploying a Web Application</h3>
                    <p>Here's an example of deploying a simple web application:</p>
                    
                    <pre><code># Connect to your Linux instance
ssh username@your-instance-ip

# Update package list
sudo apt update

# Install necessary packages
sudo apt install nginx git

# Clone your application repository
git clone https://github.com/yourusername/yourapp.git

# Configure Nginx to serve your application
sudo nano /etc/nginx/sites-available/default

# Restart Nginx
sudo systemctl restart nginx</code></pre>
                    
                    <h2 id="monitoring">Monitoring & Management</h2>
                    <p>Our platform provides comprehensive monitoring and management tools:</p>
                    
                    <ul>
                        <li><strong>Dashboard:</strong> Real-time performance metrics</li>
                        <li><strong>Alerts:</strong> Customizable notification system</li>
                        <li><strong>Logs:</strong> Centralized logging for troubleshooting</li>
                        <li><strong>Scaling:</strong> Automatic and manual scaling options</li>
                        <li><strong>Backups:</strong> Automated backup and recovery</li>
                    </ul>
                    
                    <p>Access these tools through your dashboard to ensure optimal performance of your instances.</p>
                    
                    <div class="next-prev-section">
                        <a routerLink="/documentation" class="nav-link prev">
                            <i class="bx bx-chevron-left"></i>
                            <span>Back to Documentation</span>
                        </a>
                        <a routerLink="/documentation/api" class="nav-link">
                            <span>API Documentation</span>
                            <i class="bx bx-chevron-right"></i>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>